import { SORT_DIRECTION, type SortDirection } from "@/constants/sort"
import { buildMetricNameToIdLookup } from "@/metrics/registry/identity"

import type { MetricValueFilter } from "@/lib/metricValueFilters"
import type { SortColumn } from "@/types_api/backend/transport/runs_keyset_request"
import type { MetadataFilter } from "@/types/MetadataFilter"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import { buildMultiRunFilterQuery } from "../../multi-run"
import type { RunsKeysetFilters } from "../hooks/useRunsInfiniteQuery"

export type RunsTableSelection = Set<string | number> | "all"

interface RunsTableSortDescriptor {
  column?: string | number | null
  direction?: "ascending" | "descending"
}

interface RunsDateRange {
  end: Date | null
  start: Date | null
}

interface BuildRunsKeysetFiltersParams {
  dateRange: RunsDateRange | null
  debouncedSearchValue: string
  effectiveAgentFilters: string[]
  metricIdsFilter: string[]
  metricValueFilters: MetricValueFilter[]
  mutationFilters: string[]
  personaFilters: string[]
  metadataFilters?: MetadataFilter[]
  scheduledJobFilters: string[]
  scheduledRunId?: string
  sortColumn?: SortColumn
  sortDirection: SortDirection
  statusFilters: string[]
  tagFilters: string[]
  templateFilters: string[]
  testCaseFilters: string[]
  testSetFilters: string[]
}

interface BuildRunsMultiRunReportPathParams {
  dateRange: RunsDateRange | null
  debouncedSearchValue: string
  effectiveAgentFilters: string[]
  metricIdsFilter: string[]
  metricValueFilters: MetricValueFilter[]
  mutationFilters: string[]
  organizationSlug?: string | null
  personaFilters: string[]
  metadataFilters?: MetadataFilter[]
  scheduledJobFilters: string[]
  scheduledRunId?: string
  selectedAgentId?: string | null
  simulationsOnly?: boolean
  sortColumn?: string
  sortDirection: SortDirection
  statusFilters: string[]
  tagFilters: string[]
  templateFilters: string[]
  testCaseFilters: string[]
  testSetFilters: string[]
}

export function buildDisplayNameLookup<
  T extends { id?: string | null; display_name?: string | null },
>(recordsById: Record<string, T | null | undefined>): Record<string, string> {
  const lookup: Record<string, string> = {}

  Object.values(recordsById).forEach((record) => {
    if (record?.id && record.display_name) {
      lookup[record.id] = record.display_name
    }
  })

  return lookup
}

export function buildMetricDisplayNameLookup<
  T extends {
    id?: string | null
    metric_display_name?: string | null
    metric_name?: string | null
  },
>(
  metricRecordsById: Record<string, T | null | undefined>,
  metricIdAliases: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const lookup: Record<string, string> = {}

  Object.values(metricRecordsById).forEach((metric) => {
    if (metric?.id) {
      lookup[metric.id] = resolveMetricDisplayName(
        metric.metric_display_name,
        metric.metric_name || metric.id,
      )
    }
  })

  for (const [source, canonical] of Object.entries(metricIdAliases)) {
    if (lookup[canonical]) {
      lookup[source] = lookup[canonical]
    }
  }
  return lookup
}

/**
 * Returns a resolver that maps any metric reference (record id, metric name, or
 * a legacy name from an old URL or saved view) to its canonical metric id.
 * References that match no loaded metric resolve to themselves.
 */
export function createMetricReferenceResolver<
  T extends { id?: string | null; metric_name?: string | null },
>(
  metricRecordsById: Record<string, T | null | undefined>,
  metricIdAliases: Readonly<Record<string, string>> = {},
): (reference: string) => string {
  const metricNameToIdLookup = buildMetricNameToIdLookup(metricRecordsById)

  return (reference) =>
    metricIdAliases[reference] ??
    metricRecordsById[reference]?.id ??
    metricNameToIdLookup[reference] ??
    reference
}

export function buildMetricIdsFilter<
  T extends {
    id?: string | null
    metric_name?: string | null
    metric_display_name?: string | null
  },
>(
  metricFilters: string[],
  metricRecordsById: Record<string, T | null | undefined>,
  metricIdAliases: Readonly<Record<string, string>> = {},
): string[] {
  if (metricFilters.length === 0) {
    return []
  }

  const metricNameToIdLookup = buildMetricNameToIdLookup(metricRecordsById)

  return Array.from(
    new Set(
      metricFilters
        .map((metricFilter) => {
          if (metricIdAliases[metricFilter]) {
            return metricIdAliases[metricFilter]
          }
          if (metricRecordsById[metricFilter]?.id) {
            return metricFilter
          }

          return metricNameToIdLookup[metricFilter]
        })
        .filter((metricId): metricId is string => Boolean(metricId)),
    ),
  )
}

export function findMetricRecordForFilterValue<
  T extends {
    id?: string | null
    metric_name?: string | null
    metric_display_name?: string | null
  },
>(metricFilter: string, metrics: T[]): T | undefined {
  return metrics.find(
    (metric) =>
      metric.id === metricFilter || metric.metric_name === metricFilter,
  )
}

/**
 * Maps a runs/simulations table column key (the `columnId` the @coval/ui Table's
 * sortable headers emit) to the backend keyset `sort_column`.
 *
 * Only these columns are server-sortable — the keyset query cannot order by
 * status, test set, persona, count, tags, or metric aggregates, so those keys
 * are intentionally absent (see `isSortableColumn`). Both the frontend key
 * ("created") and its legacy persisted form ("createdAt") resolve to the same
 * backend column so older saved views / URLs keep working. The simulations view
 * has no bare "id" column; its own-row identifier column ("simulationId") maps
 * to the backend "id" sort.
 */
const COLUMN_TO_SORT_COLUMN: Record<string, SortColumn> = {
  created: "createdAt",
  createdAt: "createdAt",
  agentName: "agentId",
  modelType: "modelType",
  id: "id",
  simulationId: "id",
}

/** Table column keys the keyset query can sort on (drives `sortable` headers). */
export function isSortableColumn(key: string): boolean {
  return key in COLUMN_TO_SORT_COLUMN && key !== "createdAt"
}

export function getRunsSortColumn(
  sortDescriptor: RunsTableSortDescriptor,
): SortColumn | undefined {
  const frontendSortColumn = sortDescriptor.column?.toString()

  return frontendSortColumn
    ? COLUMN_TO_SORT_COLUMN[frontendSortColumn]
    : undefined
}

/**
 * Resolves the persisted sort descriptor to the @coval/ui Table `columnId` used
 * for the initial sort indicator. Falls back to "created" for the legacy
 * "createdAt" value and any non-sortable column.
 */
export function getRunsSortColumnId(
  sortDescriptor: RunsTableSortDescriptor,
): string {
  const column = sortDescriptor.column?.toString()

  return column && isSortableColumn(column) ? column : "created"
}

export function getRunsSortDirection(
  sortDescriptor: RunsTableSortDescriptor,
): SortDirection {
  return sortDescriptor.direction === "ascending"
    ? SORT_DIRECTION.ASC
    : SORT_DIRECTION.DESC
}

interface QueryPlaceholderState {
  isFetching: boolean
  isPlaceholderData: boolean
}

export function shouldShowPlaceholderRows({
  isFetching,
  isPlaceholderData,
}: QueryPlaceholderState): boolean {
  return isPlaceholderData && isFetching
}

export function getRowsForQueryState<T>(
  rows: T[],
  state: QueryPlaceholderState,
): T[] {
  if (state.isPlaceholderData && !shouldShowPlaceholderRows(state)) {
    return []
  }

  return rows
}

export function getEffectiveAgentFilters(
  selectedAgentId: string | null,
  agentFilters: string[],
): string[] {
  return selectedAgentId ? [selectedAgentId] : agentFilters
}

export function buildRunsKeysetFilters({
  dateRange,
  debouncedSearchValue,
  effectiveAgentFilters,
  metricIdsFilter,
  metricValueFilters,
  mutationFilters,
  personaFilters,
  metadataFilters,
  scheduledJobFilters,
  scheduledRunId,
  sortColumn,
  sortDirection,
  statusFilters,
  tagFilters,
  templateFilters,
  testCaseFilters,
  testSetFilters,
}: BuildRunsKeysetFiltersParams): RunsKeysetFilters {
  return {
    searchQuery: debouncedSearchValue || undefined,
    agentFilters:
      effectiveAgentFilters.length > 0 ? effectiveAgentFilters : undefined,
    statusFilters: statusFilters.length > 0 ? statusFilters : undefined,
    templateFilters: templateFilters.length > 0 ? templateFilters : undefined,
    personaFilters: personaFilters.length > 0 ? personaFilters : undefined,
    metricFilters: metricIdsFilter.length > 0 ? metricIdsFilter : undefined,
    metricValueFilters:
      metricValueFilters.length > 0 ? metricValueFilters : undefined,
    tagFilters: tagFilters.length > 0 ? tagFilters : undefined,
    mutationFilters: mutationFilters.length > 0 ? mutationFilters : undefined,
    testSetFilters: testSetFilters.length > 0 ? testSetFilters : undefined,
    testCaseFilters: testCaseFilters.length > 0 ? testCaseFilters : undefined,
    metadataFilters: metadataFilters?.length ? metadataFilters : undefined,
    scheduledJobFilters:
      scheduledJobFilters.length > 0 ? scheduledJobFilters : undefined,
    dateRange:
      dateRange && (dateRange.start || dateRange.end)
        ? {
            start: dateRange.start || undefined,
            end: dateRange.end || undefined,
          }
        : undefined,
    scheduledRunId: scheduledRunId || undefined,
    sortColumn,
    sortDirection,
  }
}

export function buildRunsMultiRunReportPath({
  dateRange,
  debouncedSearchValue,
  effectiveAgentFilters,
  metricIdsFilter,
  metricValueFilters,
  mutationFilters,
  organizationSlug,
  personaFilters,
  metadataFilters,
  scheduledJobFilters,
  scheduledRunId,
  selectedAgentId,
  simulationsOnly,
  sortColumn,
  sortDirection,
  statusFilters,
  tagFilters,
  templateFilters,
  testCaseFilters,
  testSetFilters,
}: BuildRunsMultiRunReportPathParams): string {
  const filterQuery = buildMultiRunFilterQuery({
    searchQuery: debouncedSearchValue || undefined,
    statusFilters: statusFilters.length > 0 ? statusFilters : undefined,
    templateFilters: templateFilters.length > 0 ? templateFilters : undefined,
    personaFilters: personaFilters.length > 0 ? personaFilters : undefined,
    agentFilters:
      effectiveAgentFilters.length > 0 ? effectiveAgentFilters : undefined,
    metricFilters: metricIdsFilter.length > 0 ? metricIdsFilter : undefined,
    metricValueFilters:
      metricValueFilters.length > 0 ? metricValueFilters : undefined,
    testSetFilters: testSetFilters.length > 0 ? testSetFilters : undefined,
    testCaseFilters: testCaseFilters.length > 0 ? testCaseFilters : undefined,
    tagFilters: tagFilters.length > 0 ? tagFilters : undefined,
    mutationFilters: mutationFilters.length > 0 ? mutationFilters : undefined,
    metadataFilters: metadataFilters?.length ? metadataFilters : undefined,
    scheduledJobFilters:
      scheduledJobFilters.length > 0 ? scheduledJobFilters : undefined,
    dateRange:
      dateRange?.start || dateRange?.end
        ? {
            start: dateRange.start ?? null,
            end: dateRange.end ?? null,
          }
        : undefined,
    sortColumn: sortColumn ?? undefined,
    sortDirection,
    simulationsOnly,
  })

  const params = new URLSearchParams(filterQuery)

  if (selectedAgentId) {
    params.set("agent_id", selectedAgentId)
  }

  if (scheduledRunId) {
    params.set("scheduled_run_id", scheduledRunId)
  }

  const reportsPath = organizationSlug
    ? `/${organizationSlug}/reports/new`
    : "/reports/new"

  const queryString = params.toString()
  return queryString ? `${reportsPath}?${queryString}` : reportsPath
}

export function getRunsSelectionCount(
  selectedKeys: RunsTableSelection,
  allRunsCount: number,
): number {
  if (selectedKeys === "all") {
    return allRunsCount
  }

  if (selectedKeys instanceof Set) {
    return selectedKeys.size
  }

  return 0
}

export function getSelectedRunIds(
  selectedKeys: RunsTableSelection,
  allRunIds: string[],
): string[] {
  if (selectedKeys === "all") {
    return allRunIds
  }

  if (!(selectedKeys instanceof Set)) {
    return []
  }

  return Array.from(selectedKeys).map(String)
}

export function getSelectedRunKeySet(
  selectedKeys: RunsTableSelection,
  allRunIds: string[],
): Set<string> {
  if (selectedKeys === "all") {
    return new Set(allRunIds)
  }

  return new Set(Array.from(selectedKeys).map(String))
}
